import type { RefObject } from 'preact';
import { useEffect, useState } from 'preact/hooks';

export function useConditionalTitle<T extends HTMLElement>(
  ref: RefObject<T>,
  text: string,
): string | undefined {
  const [title, setTitle] = useState<string | undefined>(undefined);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      setTitle(undefined);
      return;
    }

    let frame = 0;
    const update = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const clippedHorizontally = el.scrollWidth > el.clientWidth + 1;
        const clippedVertically = el.scrollHeight > el.clientHeight + 1;
        setTitle(clippedHorizontally || clippedVertically ? text : undefined);
      });
    };

    update();
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(update)
      : null;
    resizeObserver?.observe(el);
    window.addEventListener('resize', update);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [ref, text]);

  return title;
}
